import {
  CalculateIsolinesCommand,
  CalculateIsolinesCommandInput,
  GeoRoutesClient
} from "@aws-sdk/client-geo-routes";
import { calculateIsolinesResponseToFeatureCollection } from "@aws/amazon-location-utilities-datatypes";
import {
  bbox,
  convex,
  lineString,
  nearestPointOnLine,
  square
} from "@turf/turf";
import CyclingIcon from "assets/icons/isochrone/ic_bike.svg";
import DrivingIcon from "assets/icons/isochrone/ic_car.svg";
import WalkingIcon from "assets/icons/isochrone/ic_walk.svg";
import { fetchAuthSession } from "aws-amplify/auth";
import { MainContextContainer } from "components/Main";
import { Polygon } from "geojson";
import { useCallback, useContext, useEffect } from "react";
import amplifyOutputs from "../../../../../../amplify_outputs.json";
import { SearchMapContext } from "../../context";
import { IsochronePluginProps } from "./interfaces";

const isochronesStore = new Map<
  string,
  {
    lat: number;
    lng: number;
    walking: boolean;
    cycling: boolean;
    driving: boolean;
    minutes: number;
    polygon?: google.maps.Polygon;
    centerMarker?: google.maps.marker.AdvancedMarkerElement;
    northWestMarker?: google.maps.marker.AdvancedMarkerElement;
  }
>();

const useHooksUsingAWS = (props: IsochronePluginProps) => {
  const { isochrones, onRemoveIsochrone } = props;

  const { setFatal } = useContext(MainContextContainer);
  const { map } = useContext(SearchMapContext);

  const renderIsochrone = useCallback(
    (
      id: string,
      coordinates: google.maps.LatLng[][],
      polygonGeometry: Polygon,
      info: {
        lat: number;
        lng: number;
        walking: boolean;
        cycling: boolean;
        driving: boolean;
        minutes: number;
      }
    ) => {
      const { lat, lng, walking, cycling, driving, minutes } = info;

      const polygon = new google.maps.Polygon({
        paths: coordinates,
        strokeColor: "#FF99FF",
        strokeOpacity: 0.8,
        strokeWeight: 3,
        fillColor: "#FF99FF",
        fillOpacity: 0.1,
        clickable: false
      });

      polygon.setMap(map);

      const squareBbox = square(
        bbox(lineString(polygonGeometry.coordinates.flat()))
      );

      const snapped = nearestPointOnLine(
        lineString(polygonGeometry.coordinates.flat()),
        [squareBbox[0], squareBbox[3]]
      );

      const snappedPoint = new google.maps.LatLng(
        snapped.geometry.coordinates[1],
        snapped.geometry.coordinates[0]
      );

      const icon = walking ? WalkingIcon : cycling ? CyclingIcon : DrivingIcon;
      const profileName = walking
        ? "徒歩"
        : cycling
          ? "自転車"
          : driving
            ? "車"
            : "不明";
      const caption = `${minutes}分`;
      const centerContent = document.createElement("div");
      const northWestContent = document.createElement("div");

      centerContent.className = "isochrone-center-marker";
      centerContent.innerHTML = `<img class="isochrone-icon" src="${icon}" alt="${profileName}" />`;

      northWestContent.className = "isochrone-northwest-marker";
      northWestContent.innerHTML = `<img class="isochrone-icon" src="${icon}" alt="${profileName}" /><span class="isochrone-title">${caption}</span>`;

      const centerMarker = new google.maps.marker.AdvancedMarkerElement({
        position: new google.maps.LatLng(lat, lng),
        content: centerContent
      });

      const northWestMarker = new google.maps.marker.AdvancedMarkerElement({
        position: snappedPoint,
        content: northWestContent
      });

      const onClick = () => {
        const { polygon, centerMarker, northWestMarker } =
          isochronesStore.get(id) || {};

        if (polygon) {
          polygon.setMap(null);
        }

        if (centerMarker) {
          centerMarker.map = null;
        }

        if (northWestMarker) {
          northWestMarker.map = null;
        }

        isochronesStore.delete(id);
        onRemoveIsochrone?.(id);
      };

      centerMarker.addListener("gmp-click", onClick);
      centerMarker.map = map;

      northWestMarker.addListener("gmp-click", onClick);
      northWestMarker.map = map;

      isochronesStore.set(id, {
        lat,
        lng,
        walking,
        cycling,
        driving,
        minutes,
        polygon,
        centerMarker,
        northWestMarker
      });
    },
    [map, onRemoveIsochrone]
  );

  const drawIsochroneUsingAWS = useCallback(
    async (
      id: string,
      lat: number,
      lng: number,
      walking: boolean,
      cycling: boolean,
      driving: boolean,
      minutes: number
    ) => {
      if (!map) {
        return;
      }

      if (isochronesStore.has(id)) {
        return;
      }

      const session = await fetchAuthSession();
      const geoRoutesClient = new GeoRoutesClient({
        region: amplifyOutputs.auth.aws_region,
        credentials: session.credentials
      });

      const CalculateIsolinesCommandInput: CalculateIsolinesCommandInput = {
        Origin: [lng, lat],
        Thresholds: {
          Time: [
            cycling
              ? minutes * 60 * (250 / 80) // 自転車がないので徒歩で代用 (自転車: 250m/min, 徒歩: 80m/min)
              : minutes * 60 // 秒
          ]
        },
        TravelMode: walking
          ? "Pedestrian"
          : cycling
            ? "Pedestrian" // 自転車がないので徒歩で代用
            : driving
              ? "Car"
              : undefined
      };

      const calculateIsolinesCommand = new CalculateIsolinesCommand(
        CalculateIsolinesCommandInput
      );

      const response = await geoRoutesClient
        .send(calculateIsolinesCommand)
        .catch((error) => {
          window.console.error(error);
          setFatal(`到達圏の取得に失敗しました: ${error}`);
          return null;
        });

      if (response) {
        const data = calculateIsolinesResponseToFeatureCollection(response, {
          flattenProperties: true
        });

        let count = 0;

        if (data && data.features && data.features.length > 0) {
          for (const feature of data.features) {
            if (
              "coordinates" in feature.geometry &&
              feature.geometry.coordinates.length > 0
            ) {
              const simplified = convex(feature.geometry, {
                concavity: 3
              })?.geometry;

              if (simplified) {
                const coordinates = simplified.coordinates.map((coord) =>
                  coord.map(
                    (coord) => new google.maps.LatLng(coord[1], coord[0])
                  )
                );

                if (coordinates.length > 0) {
                  renderIsochrone(id, coordinates, simplified, {
                    lat,
                    lng,
                    walking,
                    cycling,
                    driving,
                    minutes
                  });

                  count++;
                }
              }
            }
          }
        }

        if (count === 0) {
          setFatal("有効な到達圏を取得できませんでした。");
        }
      }
    },
    [map, renderIsochrone, setFatal]
  );

  // const drawIsochroneUsingCircle = useCallback(
  //   async (
  //     id: string,
  //     lat: number,
  //     lng: number,
  //     walking: boolean,
  //     cycling: boolean,
  //     driving: boolean,
  //     minutes: number
  //   ) => {
  //     if (!map) {
  //       return;
  //     }

  //     if (isochronesStore.has(id)) {
  //       return;
  //     }

  //     const radius = walking
  //       ? minutes * 80 // 徒歩
  //       : cycling
  //       ? minutes * 250 // 自転車
  //       : driving
  //       ? minutes * 400 // 車
  //       : undefined;

  //     if (radius === undefined) {
  //       setFatal("移動手段が選択されていません。");
  //       return;
  //     }

  //     const circleGeometry = circle([lng, lat], radius * 0.776, {
  //       units: "meters"
  //     });

  //     const coordinates = circleGeometry.geometry.coordinates.map((coord) =>
  //       coord.map((c) => new google.maps.LatLng(c[1], c[0]))
  //     );

  //     renderIsochrone(id, coordinates, circleGeometry.geometry, {
  //       lat,
  //       lng,
  //       walking,
  //       cycling,
  //       driving,
  //       minutes
  //     });
  //   },
  //   [map, renderIsochrone, setFatal]
  // );

  const drawIsochrones = useCallback(async () => {
    if (map) {
      for (const [id, isochrone] of isochrones.entries()) {
        await drawIsochroneUsingAWS(
          id,
          isochrone.lat,
          isochrone.lng,
          isochrone.walking,
          isochrone.cycling,
          isochrone.driving,
          isochrone.minutes
        );
      }
    }
  }, [drawIsochroneUsingAWS, isochrones, map]);

  useEffect(() => {
    if (map) {
      drawIsochrones();
    }
  }, [drawIsochrones, map]);

  useEffect(() => {
    return () => {
      isochronesStore.forEach((value, id) => {
        if (value.polygon) {
          value.polygon.setMap(null);
        }

        if (value.centerMarker) {
          value.centerMarker.map = null;
        }

        if (value.northWestMarker) {
          value.northWestMarker.map = null;
        }

        isochronesStore.delete(id);

        onRemoveIsochrone?.(id);
      });
      isochronesStore.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { map } as const;
};

export const useHooks = useHooksUsingAWS;
